// Breadcrumbs — auto-derived Dashboard › Section › Page trail rendered
// above every PageHeader. Reads the current location and matches the
// most specific pattern in ROUTE_LABELS; a page can also pass an
// explicit `trail` prop (array of {label, to?}) to override.
//
// Design choices:
//   * The first crumb is always "Dashboard" — matches the app's mental
//     model where the dashboard is the home base.
//   * We keep the map short and pattern-based (":id" placeholders) so
//     it stays useful without exploding into per-route noise.

import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Home, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePublishedTrail } from "@/context/BreadcrumbContext";

// Iter 108 — Session-scoped cache of `slug|app_id -> friendly name` so
// the /apps/* breadcrumb never surfaces the raw `app_9cfcaafd67` id.
// Populated once per page load and refreshed on demand when a new app
// key shows up.
const _appLabelCache = { byRef: new Map(), loaded: false, inflight: null };

async function _loadAppLabels() {
  if (_appLabelCache.inflight) return _appLabelCache.inflight;
  _appLabelCache.inflight = (async () => {
    try {
      const { data } = await api.get("/apps");
      (data || []).forEach((a) => {
        if (a.name) {
          if (a.app_id) _appLabelCache.byRef.set(a.app_id, a.name);
          if (a.slug)   _appLabelCache.byRef.set(a.slug, a.name);
        }
      });
      _appLabelCache.loaded = true;
    } catch { /* silent — breadcrumb falls back to humanised segment */ }
    _appLabelCache.inflight = null;
  })();
  return _appLabelCache.inflight;
}

const KB = { label: "Knowledge Base", to: "/kb" };

// Longest keys win — patterns use "*" for a wildcard segment.
const ROUTE_LABELS = [
  { pattern: "/dashboard",              trail: [] },
  { pattern: "/apps",                   trail: [{ label: "Apps", to: "/apps" }] },
  { pattern: "/apps/*",                 trail: [{ label: "Apps", to: "/apps" }, { label: "App" }] },
  { pattern: "/partners",               trail: [{ label: "Partners", to: "/partners" }] },
  { pattern: "/partners/*",             trail: [{ label: "Partners", to: "/partners" }, { label: "Partner" }] },
  { pattern: "/customers",              trail: [{ label: "Customers", to: "/customers" }] },
  { pattern: "/customers/*",            trail: [{ label: "Customers", to: "/customers" }, { label: "Customer" }] },
  { pattern: "/users",                  trail: [{ label: "Users", to: "/users" }] },
  { pattern: "/organizations",          trail: [{ label: "Organizations", to: "/organizations" }] },
  { pattern: "/permissions",            trail: [{ label: "Permissions", to: "/permissions" }] },
  { pattern: "/activity",               trail: [{ label: "Activity", to: "/activity" }] },
  { pattern: "/analytics",              trail: [{ label: "Analytics", to: "/analytics" }] },
  { pattern: "/requests",               trail: [{ label: "Requests", to: "/requests" }] },
  { pattern: "/developer",              trail: [{ label: "Developer", to: "/developer" }] },
  { pattern: "/quotes",                 trail: [{ label: "Quotes", to: "/quotes" }] },
  { pattern: "/projects",               trail: [{ label: "Projects", to: "/projects" }] },
  { pattern: "/tasks",                  trail: [{ label: "Tasks", to: "/tasks" }] },
  { pattern: "/tickets",                trail: [{ label: "Tickets", to: "/tickets" }] },
  { pattern: "/tickets/*",              trail: [{ label: "Tickets", to: "/tickets" }, { label: "Ticket" }] },
  { pattern: "/messages",               trail: [{ label: "Messages", to: "/messages" }] },
  { pattern: "/commissions",            trail: [{ label: "Commissions", to: "/commissions" }] },
  { pattern: "/commissions/payments",   trail: [{ label: "Commissions", to: "/commissions" }, { label: "Payments Timeline" }] },
  { pattern: "/commissions/engine",     trail: [{ label: "Commissions", to: "/commissions" }, { label: "Engine" }] },
  { pattern: "/commissions/partners/*", trail: [{ label: "Commissions", to: "/commissions" }, { label: "Partner" }] },
  { pattern: "/qbr",                    trail: [{ label: "QBR", to: "/qbr" }] },
  { pattern: "/qbr/*",                  trail: [{ label: "QBR", to: "/qbr" }, { label: "Deck" }] },
  { pattern: "/hardware",               trail: [{ label: "Hardware", to: "/hardware" }] },
  { pattern: "/inventory-hub",          trail: [{ label: "Inventory Hub", to: "/inventory-hub" }] },
  { pattern: "/inventory-hub/hardware",       trail: [{ label: "Inventory Hub", to: "/inventory-hub" }, { label: "Hardware" }] },
  { pattern: "/inventory-hub/sip-trunks",     trail: [{ label: "Inventory Hub", to: "/inventory-hub" }, { label: "SIP Trunks" }] },
  { pattern: "/inventory-hub/manufacturers",  trail: [{ label: "Inventory Hub", to: "/inventory-hub" }, { label: "Manufacturers" }] },
  { pattern: "/inventory-hub/did-master",     trail: [{ label: "Inventory Hub", to: "/inventory-hub" }, { label: "DID Master" }] },
  { pattern: "/inventory-hub/network-keys",   trail: [{ label: "Inventory Hub", to: "/inventory-hub" }, { label: "Network Assessment Keys" }] },
  { pattern: "/number-orders",          trail: [{ label: "Number Orders", to: "/number-orders" }] },
  { pattern: "/fax",                    trail: [{ label: "Fax", to: "/fax" }] },
  { pattern: "/in-out",                 trail: [{ label: "Who's In", to: "/in-out" }] },
  { pattern: "/did-master",             trail: [{ label: "DID Master", to: "/did-master" }] },
  { pattern: "/key-master",             trail: [{ label: "API Key Master", to: "/key-master" }] },
  { pattern: "/sip-trace",              trail: [{ label: "SIP Trace", to: "/sip-trace" }] },
  { pattern: "/sip-trace-analysis",     trail: [{ label: "SIP Trace Analysis", to: "/sip-trace-analysis" }] },
  { pattern: "/service-desk",           trail: [{ label: "Service Desk", to: "/service-desk" }] },
  { pattern: "/ask-claude",             trail: [{ label: "Ask AI", to: "/ask-claude" }] },
  { pattern: "/ai-outbound",            trail: [{ label: "AI Outbound", to: "/ai-outbound" }] },
  { pattern: "/ai-outbound/*",          trail: [{ label: "AI Outbound", to: "/ai-outbound" }, { label: "Campaign" }] },
  { pattern: "/uc-hospitality",         trail: [{ label: "UC Hospitality", to: "/uc-hospitality" }] },
  { pattern: "/changelog",              trail: [{ label: "Changelog", to: "/changelog" }] },
  { pattern: "/territory-aliases",      trail: [{ label: "Territory Aliases", to: "/territory-aliases" }] },
  { pattern: "/env-drift",              trail: [{ label: "Environment Drift", to: "/env-drift" }] },
  { pattern: "/humi",                   trail: [{ label: "Humi", to: "/humi" }] },
  { pattern: "/humi/home",              trail: [{ label: "Humi", to: "/humi" }, { label: "Home" }] },
  { pattern: "/humi/profile",           trail: [{ label: "Humi", to: "/humi" }, { label: "Profile" }] },
  { pattern: "/humi/people",            trail: [{ label: "Humi", to: "/humi" }, { label: "People" }] },
  { pattern: "/humi/time-off",          trail: [{ label: "Humi", to: "/humi" }, { label: "Time Off" }] },
  { pattern: "/humi/documents",         trail: [{ label: "Humi", to: "/humi" }, { label: "Documents" }] },
  { pattern: "/humi/training",          trail: [{ label: "Humi", to: "/humi" }, { label: "Training" }] },
  { pattern: "/humi/surveys",           trail: [{ label: "Humi", to: "/humi" }, { label: "Surveys" }] },
  { pattern: "/humi/performance",       trail: [{ label: "Humi", to: "/humi" }, { label: "Performance" }] },
  { pattern: "/humi/reporting",         trail: [{ label: "Humi", to: "/humi" }, { label: "Reporting" }] },
  { pattern: "/humi/benefits",          trail: [{ label: "Humi", to: "/humi" }, { label: "Benefits" }] },
  { pattern: "/humi/company",           trail: [{ label: "Humi", to: "/humi" }, { label: "Company" }] },
  { pattern: "/humi/addons",            trail: [{ label: "Humi", to: "/humi" }, { label: "Add-ons" }] },
  { pattern: "/help",                   trail: [{ label: "Help", to: "/help" }] },
  { pattern: "/settings",               trail: [{ label: "Settings", to: "/settings" }] },
  { pattern: "/jobs",                   trail: [{ label: "Job Board", to: "/jobs" }] },
  { pattern: "/emergency-numbers",      trail: [{ label: "Emergency Numbers", to: "/emergency-numbers" }] },
  { pattern: "/jobs/*",                 trail: [{ label: "Job Board", to: "/jobs" }, { label: "Posting" }] },
  { pattern: "/reports",                trail: [{ label: "Reports", to: "/reports" }] },
  { pattern: "/reports/*",              trail: [{ label: "Reports", to: "/reports" }, { label: "Report" }] },
  { pattern: "/clients",                trail: [{ label: "Clients", to: "/clients" }] },
  { pattern: "/inventory",              trail: [{ label: "Inventory", to: "/inventory" }] },
  { pattern: "/helpdesk",               trail: [{ label: "Helpdesk", to: "/helpdesk" }] },
  { pattern: "/helpdesk/*",             trail: [{ label: "Helpdesk", to: "/helpdesk" }, { label: "Ticket" }] },
  { pattern: "/productivity",           trail: [{ label: "Productivity", to: "/productivity" }] },
  { pattern: "/profile",                trail: [{ label: "My Profile", to: "/profile" }] },
  { pattern: "/teams",                  trail: [{ label: "Teams", to: "/teams" }] },
  { pattern: "/teams/*",                trail: [{ label: "Teams", to: "/teams" }, { label: "Team" }] },
  { pattern: "/data-sync",              trail: [{ label: "Data Sync", to: "/data-sync" }] },
  { pattern: "/oauth-debug",            trail: [{ label: "OAuth Live Debug", to: "/oauth-debug" }] },
  { pattern: "/tlms-debug",             trail: [{ label: "TalentLMS Debug", to: "/tlms-debug" }] },
  // Iter 239 — Knowledge Base. Reader routes keep their own in-page category
  // trail; these give every KB screen the same "Dashboard › Knowledge Base ›"
  // prefix as the rest of the app.
  { pattern: "/kb",                     trail: [KB] },
  { pattern: "/kb/new",                 trail: [KB, { label: "New Article" }] },
  { pattern: "/kb/a/*",                 trail: [KB, { label: "Article" }] },
  { pattern: "/kb/a/*/edit",            trail: [KB, { label: "Article" }, { label: "Edit" }] },
  { pattern: "/kb/c/*",                 trail: [KB, { label: "Category" }] },
  { pattern: "/kb/c/*/print",           trail: [KB, { label: "Category" }, { label: "Print" }] },
  { pattern: "/kb/studio",              trail: [KB, { label: "Compose" }] },
  { pattern: "/kb/snippets",            trail: [KB, { label: "Snippets Library" }] },
  { pattern: "/kb/planner",             trail: [KB, { label: "Planner" }] },
  { pattern: "/kb/callout-audit",       trail: [KB, { label: "Callout Audit" }] },
  { pattern: "/kb/cleaned",             trail: [KB, { label: "Recently Cleaned" }] },
  { pattern: "/kb/image-health",        trail: [KB, { label: "Image & Link Health" }] },
  { pattern: "/kb/suggestions",         trail: [KB, { label: "Suggestions" }] },
  { pattern: "/kb/visibility",          trail: [KB, { label: "Visibility" }] },
  { pattern: "/kb/bulk-visibility",     trail: [KB, { label: "Visibility", to: "/kb/visibility" }, { label: "Bulk Update" }] },
  { pattern: "/kb/category-health",     trail: [KB, { label: "Health" }] },
  { pattern: "/kb/search-insights",     trail: [KB, { label: "Insights" }] },
  { pattern: "/kb/usage",               trail: [KB, { label: "Usage" }] },
  { pattern: "/kb/manage",              trail: [KB, { label: "Categories" }] },
  { pattern: "/kb/manage/articles",     trail: [KB, { label: "Articles" }] },
  { pattern: "/kb/duplicates",          trail: [KB, { label: "Find Duplicates" }] },
  { pattern: "/kb/feedback",            trail: [KB, { label: "Feedback" }] },
  { pattern: "/kb/glossary",            trail: [KB, { label: "Glossary" }] },
  { pattern: "/kb/danger-review",       trail: [KB, { label: "Danger Review" }] },
  { pattern: "/kb/risk-review",         trail: [KB, { label: "Risk Review" }] },
  { pattern: "/kb/missing-assets",      trail: [KB, { label: "Missing Assets" }] },
];

function matchPattern(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  // Prefer the longest matching pattern.
  const scored = ROUTE_LABELS
    .map((entry) => {
      const parts = entry.pattern.split("/").filter(Boolean);
      if (parts.length !== segs.length) return null;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i] !== "*" && parts[i] !== segs[i]) return null;
      }
      return { entry, score: parts.length };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);
  return scored[0]?.entry || null;
}

// Turn "sip-trunks" into "SIP Trunks" for the final crumb when we don't
// have a nicer label for the dynamic segment. Small, safe helper.
function humanise(seg) {
  if (!seg) return "";
  // Iter 108 — Never surface opaque `app_9cfcaafd67`-style ids as a
  // breadcrumb; return empty so the caller falls through to a better
  // label (the app's real name from _appLabelCache) or hides the crumb.
  if (/^app_[a-f0-9]{6,}$/i.test(seg)) return "";
  const upper = new Set(["sip", "cdr", "voip", "ai", "id", "qbr", "lnp", "did", "csv", "pbx", "mfa", "sso"]);
  return seg
    .split("-")
    .map((w) => (upper.has(w.toLowerCase()) ? w.toUpperCase() : w[0]?.toUpperCase() + w.slice(1)))
    .join(" ");
}

export function Breadcrumbs({ trail, current, hideOnDashboard = true }) {
  const location = useLocation();
  const params = useParams();
  const [, setTick] = useState(0);
  const { user } = useAuth();
  const published = usePublishedTrail();
  // Iter 111 — Partner-role viewers see "Staff" instead of "Partners"
  // in the breadcrumbs (matches the Layout nav rebrand).
  const isPartnerViewer = (user?.base_role || user?.role) === "partner";

  // Iter 108 — Resolve the app-id/slug in the URL to the app's real
  // display name so breadcrumbs never show `app_9cfcaafd67`.
  const appRef = params.appId;
  useEffect(() => {
    if (!appRef) return;
    if (_appLabelCache.byRef.has(appRef)) return;
    _loadAppLabels().then(() => setTick((t) => t + 1));
  }, [appRef]);

  // Skip on the dashboard itself — the crumb chain would just be "Home".
  if (hideOnDashboard && location.pathname.replace(/\/+$/, "") === "/dashboard") {
    return null;
  }

  let items;
  if (Array.isArray(trail)) {
    items = trail;
  } else if (Array.isArray(published) && published.length) {
    items = published;
  } else {
    const matched = matchPattern(location.pathname);
    items = matched ? [...matched.trail] : [];
    // If the last crumb is a placeholder and we have a dynamic id, use it
    // (or the id itself as a last-resort label).
    const appLabel = appRef ? _appLabelCache.byRef.get(appRef) : "";
    const dynLabel = current
      || humanise(params.reportKey)
      || humanise(params.postingId)
      || appLabel
      || humanise(params.appId)
      || humanise(params.id)
      || humanise(params.name);
    if (dynLabel && items.length && !items[items.length - 1].to) {
      items[items.length - 1] = { ...items[items.length - 1], label: dynLabel };
    }
  }

  // Iter 111 — Rebrand "Partners" → "Staff" in the crumb trail for
  // partner-role viewers so it matches the Layout nav label.
  if (isPartnerViewer) {
    items = items.map((it) => (
      it.to === "/partners" && it.label === "Partners"
        ? { ...it, label: "Staff" }
        : it
    ));
  }

  // Iter 130 — a user may reach /apps/<app> via its own permission without
  // holding apps.view; don't offer a crumb link they'd only get a 403 from.
  if (!(user?.permissions || []).includes("apps.view")) {
    items = items.map((it) => (it.to === "/apps" ? { ...it, to: undefined } : it));
  }

  return (
    <nav aria-label="Breadcrumb" className="mb-3 text-xs" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1 text-slate-500 dark:text-slate-400">
        <li>
          <Link to="/dashboard"
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                data-testid="breadcrumb-home">
            <Home className="h-3.5 w-3.5" />
            <span>Dashboard</span>
          </Link>
        </li>
        {items.map((item, idx) => {
          const last = idx === items.length - 1;
          return (
            <React.Fragment key={`${item.label}-${idx}`}>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />
              <li>
                {item.to && !last ? (
                  <Link to={item.to}
                        className="rounded px-1.5 py-0.5 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                        data-testid={`breadcrumb-${slug(item.label)}`}>
                    {item.label}
                  </Link>
                ) : (
                  <span className="rounded px-1.5 py-0.5 font-medium text-slate-800 dark:text-slate-100"
                        data-testid={`breadcrumb-${slug(item.label)}`}
                        aria-current={last ? "page" : undefined}>
                    {item.label}
                  </span>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
